/**
 * Step 2's panel (handoff §4.4 a–e; desktop «Выбрано» card, §19.3): what the last
 * tap selected and its role. One component for both layouts; the page puts it in
 * the pinned bottom bar on a phone and in the right column on desktop.
 */
import type { ReactNode } from 'react'
import { useLocale } from '../../i18n/context'
import CEFRBadge from '../shared/CEFRBadge'
import type { Role } from '../../utils/textMarks'
import { Eyebrow, GhostButton, OutlinePill, Segmented } from './ui'

export type PanelView =
  | { kind: 'idle' }
  | {
      kind: 'word'
      text: string
      cefr: string | null
      /** Shown only when it differs from the word as written. */
      lemma: string | null
      translation: string | null
      role: Role | null
      gapPlaces: number | null
      gapOnly: boolean
      /** Edit mode: the receiving deck already holds its card, so it can only be a gap (Amendment 3). */
      cardedIn?: string | null
    }
  | { kind: 'phrase'; text: string; words: number; role: Role; gapPlaces: number | null; cardedIn?: string | null }
  | { kind: 'waiting'; text: string }
  | { kind: 'removed'; text: string }

interface Props {
  view: PanelView
  desktop: boolean
  onRole: (role: Role) => void
  onRemove: () => void
  onMakePhrase: () => void
  onCancelWaiting: () => void
  onRestore: () => void
  /**
   * Edit mode: removing a mark is «Снять отметку», never «Убрать», so nothing reads as a
   * card being deleted (ADR-115 Amendment 3 Decision 5).
   */
  edit?: boolean
}

const ROLES: Role[] = ['card', 'gap', 'both']

function PhraseChip({ children }: { children: ReactNode }) {
  return (
    <span
      style={{
        display: 'inline-flex', alignItems: 'center', height: 20, padding: '0 8px', flexShrink: 0,
        borderRadius: 'var(--radius-pill)', background: 'var(--accent-soft)', color: 'var(--accent)',
        fontSize: 11, fontWeight: 700, letterSpacing: '0.02em', textTransform: 'uppercase',
      }}
    >
      {children}
    </span>
  )
}

export default function MarkPanel({ view, desktop, onRole, onRemove, onMakePhrase, onCancelWaiting, onRestore, edit = false }: Props) {
  const { tr } = useLocale()
  const t = tr.textFlow.words.panel
  const removeLabel = edit ? tr.textEdit.unmark : t.remove
  const muted = { margin: 0, fontSize: 13, lineHeight: 1.4, color: 'var(--text-secondary)' } as const
  const term = (text: string, nowrap = false) => (
    <span
      style={{
        fontFamily: 'var(--font-content)', fontSize: desktop ? 22 : 20, fontWeight: 700, color: 'var(--text-primary)',
        minWidth: 0, ...(nowrap ? { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } : {}),
      }}
    >
      {text}
    </span>
  )

  const cardedNote = (deck: string | null | undefined) =>
    deck != null && <p style={muted} data-testid="panel-carded">{tr.textEdit.cardedIn(deck)}</p>

  const roleRow = (role: Role | null, gapOnly = false) => (
    // The remove action wraps under the roles when the row is too narrow for both, so no
    // label is ever cut or overflows its pill (360 px, any locale).
    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 8, rowGap: 4 }}>
      <div style={{ flex: '1 1 auto', minWidth: 0 }}>
        <Segmented<Role>
          label={t.role}
          inset={3}
          fit
          height={desktop ? 40 : 44}
          value={role}
          onChange={onRole}
          options={ROLES.filter((r) => !gapOnly || r === 'gap').map((r) => ({ id: r, label: t.roles[r], testId: `role-${r}` }))}
        />
      </div>
      {!desktop && <GhostButton onClick={onRemove} data-testid="panel-remove" style={{ marginLeft: 'auto' }}>{removeLabel}</GhostButton>}
    </div>
  )

  const desktopActions = (phrase: boolean) =>
    desktop && (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {!phrase && <OutlinePill onClick={onMakePhrase} data-testid="make-phrase">{t.makePhrase}</OutlinePill>}
        <span style={{ flex: 1 }} />
        <GhostButton onClick={onRemove} data-testid="panel-remove">{removeLabel}</GhostButton>
      </div>
    )

  let body: ReactNode
  if (view.kind === 'idle') {
    body = <p style={muted} data-testid="panel-idle">{desktop ? t.idleDesktop : t.idle}</p>
  } else if (view.kind === 'removed') {
    body = (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }} role="status">
        <span style={{ fontSize: 14, color: 'var(--text-secondary)' }}>{edit ? tr.textEdit.unmarked(view.text) : t.removed(view.text)}</span>
        <GhostButton onClick={onRestore} data-testid="panel-restore" style={{ flexShrink: 0 }}>{t.restore}</GhostButton>
      </div>
    )
  } else if (view.kind === 'waiting') {
    body = (
      <>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 36 }}>
          {term(`${view.text} …`)}
          <PhraseChip>{t.phrase}</PhraseChip>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <span style={{ fontSize: 14, color: 'var(--text-primary)' }} data-testid="panel-waiting">{t.waiting}</span>
          <GhostButton onClick={onCancelWaiting} data-testid="panel-cancel" style={{ flexShrink: 0 }}>{t.cancel}</GhostButton>
        </div>
      </>
    )
  } else if (view.kind === 'word') {
    const gloss = [view.lemma, view.translation ?? t.noTranslation].filter(Boolean).join(' · ')
    body = (
      <>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 36 }}>
          {term(view.text)}
          <CEFRBadge level={view.cefr} />
          <span style={{ flex: 1 }} />
          {!desktop && !view.gapOnly && (
            <OutlinePill onClick={onMakePhrase} data-testid="make-phrase">{t.makePhrase}</OutlinePill>
          )}
        </div>
        <p style={muted} data-testid="panel-gloss">{view.gapOnly ? t.gapOnly : gloss}</p>
        {view.gapPlaces !== null && <p style={muted} data-testid="panel-gap-places">{t.gapPlaces(view.gapPlaces)}</p>}
        {cardedNote(view.cardedIn)}
        {roleRow(view.role, view.gapOnly || view.cardedIn != null)}
        {desktopActions(false)}
      </>
    )
  } else {
    body = (
      <>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 36, flexWrap: desktop ? 'wrap' : 'nowrap' }}>
          {term(view.text, !desktop)}
          <PhraseChip>{t.phraseCount(view.words)}</PhraseChip>
        </div>
        {view.role !== 'gap' && view.cardedIn == null && <p style={muted} data-testid="panel-in-card">{t.inCard(view.text, t.noTranslation)}</p>}
        {view.gapPlaces !== null && <p style={muted} data-testid="panel-gap-places">{t.gapPlaces(view.gapPlaces)}</p>}
        {cardedNote(view.cardedIn)}
        {roleRow(view.role, view.cardedIn != null)}
        {desktopActions(true)}
      </>
    )
  }

  return (
    <div data-testid="mark-panel" style={{ display: 'flex', flexDirection: 'column', gap: desktop ? 10 : 12 }}>
      {desktop && view.kind !== 'removed' && (
        <Eyebrow>{view.kind === 'phrase' || view.kind === 'waiting' ? t.selectedPhrase : t.selected}</Eyebrow>
      )}
      {body}
    </div>
  )
}
